/* eslint-disable no-nested-ternary */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { camelCase, forEach, drop } from 'lodash';
import { all, spread } from 'axios';

import {
  adminInstance as Admin,
  referenceAdminInstance as RefAdmin,
} from '~/utils';

const SliceContext = React.createContext();

const availableSlices = [
  'market',
  'specialty',
  'sub-specialty',

  'organization',
  'division',
];

const fetch = async (type, params = {}) => {
  const options = {
    'is-active': true,
    page: 1,
    'page-size': 10000,
    ...params,
  };

  const namePrefix = type === 'organization' ? 'org' : 'division';

  switch (type) {
    case 'market':
    case 'specialty':
    case 'sub-specialty':
      return RefAdmin
        .get(`/reference-${type}${params.id ? `/${params.id}` : ''}`, {
          params: !params.id ? {
            sort: `${type}-label`,
            ...options,
          } : undefined,
        })
        .then(r => (Array.isArray(r.data.data)
          ? {
            ...r,
            data: {
              ...r.data,
              data: r.data.data.map(d => ({
                ...d,
                label: d.attributes[camelCase(`${type}Label`)],
              })),
            },
          }
          : {
            ...r,
            data: {
              ...r.data,
              data: {
                ...r.data.data,
                label: r.data.data.attributes[camelCase(`${type}Label`)],
              },

            },
          }));

    case 'organization':
    case 'division':
      return Admin
        .get(`/${type}${params.id ? `/${params.id}` : ''}`, {
          params: !params.id ? {
            sort: `${namePrefix}-name`,
            ...options,
          } : undefined,
        })
        .then(r => (Array.isArray(r.data.data)
          ? {
            ...r,
            data: {
              ...r.data,
              data: r.data.data.map(d => ({
                ...d,
                label: d.attributes[camelCase(`${namePrefix}Name`)],
              })),
            },
          }
          : {
            ...r,
            data: {
              ...r.data,
              data: {
                ...r.data.data,
                label: r.data.data.attributes[camelCase(`${namePrefix}Name`)],
              },

            },
          }));

    default:
      return '';
  }
};

const marketLineage = ['market', 'specialty', 'sub-specialty'];
const organizationLineage = ['organization', 'division'];

const calculateLineage = (slice) => {
  let indexOf;

  switch (slice) {
    case 'market':
    case 'specialty':
    case 'sub-specialty':
      indexOf = marketLineage.indexOf(slice);

      return {
        next: marketLineage[indexOf + 1],
        prev: marketLineage[indexOf - 1],
        lineage: marketLineage,
      };

    case 'organization':
    case 'division':
      indexOf = organizationLineage.indexOf(slice);

      return {
        next: organizationLineage[indexOf + 1],
        prev: organizationLineage[indexOf - 1],
        lineage: organizationLineage,
      };

    default:
      return 'system';
  }
};

const attachToItem = (item) => {
  const promises = [item];

  const {
    marketUid, specialtyUid, subSpecialtyUid, orgUid, divisionUid,
  } = item.attributes;

  if (marketUid) {
    promises.push(fetch('market', { id: marketUid }));
  }

  if (specialtyUid) {
    promises.push(fetch('specialty', { id: specialtyUid }));
  }

  if (subSpecialtyUid) {
    promises.push(fetch('sub-specialty', { id: subSpecialtyUid }));
  }

  if (orgUid) {
    promises.push(fetch('organization', { id: orgUid }));
  }

  if (divisionUid) {
    promises.push(fetch('division', { id: divisionUid }));
  }

  return all(promises)
    .then(spread((r, ...slices) => {
      const d = r;

      d.attributes.org = null;
      d.attributes.division = null;
      d.attributes.market = null;
      d.attributes.specialty = null;
      d.attributes.subSpecialty = null;

      forEach(slices, ({ data: { data: s } }) => {
        const { type: t } = s;
        const type = t.startsWith('reference') ? camelCase(drop(t.split('-')).join('-')) : t === 'organization' ? 'org' : t;

        d.attributes[type] = s;
      });

      return d;
    }));
};

const SliceProvider = ({ children }) => {
  const [markets, setMarkets] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [subSpecialties, setSubSpecialties] = useState([]);

  const [organizations, setOrganizations] = useState([]);
  const [divisions, setDivisions] = useState([]);

  const [current, setCurrent] = useState({});

  const f = (type, params = {}) =>
    fetch(type, params)
      .then((r) => {
        if (!params.id) {
          if (type === 'market') {
            setMarkets(r.data.data);
          } else if (type === 'specialty') {
            setSpecialties(r.data.data);
          } else if (type === 'sub-specialty') {
            setSubSpecialties(r.data.data);
          } else if (type === 'organization') {
            setOrganizations(r.data.data);
          } else if (type === 'division') {
            setDivisions(r.data.data);
          }
        }

        return r;
      });

  return (
    <SliceContext.Provider
      value={{
        markets,
        setMarkets,

        specialties,
        setSpecialties,

        subSpecialties,
        setSubSpecialties,

        organizations,
        setOrganizations,

        divisions,
        setDivisions,

        fetch: (type, params = {}) =>
          f(type, params),

        fetchAll: (slices = availableSlices, params = {}) =>
          slices.forEach(s =>
            f(s, params[s])),

        currentSlice: current,
        setCurrentSlice: nextSlice =>
          (typeof nextSlice === 'object'
            ? setCurrent(nextSlice)
            : setCurrent({ type: nextSlice })
          ),

        calculateLineage: slice =>
          calculateLineage(slice),

        attachToItem,
      }}
    >
      {children}
    </SliceContext.Provider>
  );
};

SliceProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SliceProvider;
export { SliceContext };
